import { AxiosError } from "axios";
import { baseFetch } from "./base";
import { extractAssessmentQuestion } from "../utils/assessmentInteractive";
import type {
  InteractionSubmitPayload,
} from "./interaction";

export interface Answer {
  text: string;
  points: number;
}

export interface ScoringRubric {
  advanced: string;
  professional: string;
  expert: string;
}

export interface PreassessmentQuestion {
  grading_guidance: string;
  language: string;
  answers: Answer[];
  scoring_rubric: ScoringRubric;
  question: string;
  competency: string;
}

export interface PreassessmentData {
  questions?: PreassessmentQuestion[];
  total_questions_number: number;
  answered_questions_number: number;
  scores_earned: number;
}

export interface PreassessmentAnswerPayload {
  answers: Array<{
    concept: string;
    score: number;
    all_score: number;
  }>;
}

export type AssessmentQuestionType =
  | "msq"
  | "open_text"
  | "fill_in_blank"
  | "ordering"
  | "matching"
  | "ordered_response";

export const DEFAULT_ASSESSMENT_QUESTION_TYPE: AssessmentQuestionType = "msq";

const LEGACY_ASSESSMENT_QUESTION_TYPE_ALIASES: Record<
  string,
  AssessmentQuestionType
> = {
  multiple_choice: "msq",
};

export const normalizeAssessmentQuestionType = (
  value: unknown,
): AssessmentQuestionType | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const normalized =
    LEGACY_ASSESSMENT_QUESTION_TYPE_ALIASES[value] ?? value;
  const validTypes: AssessmentQuestionType[] = [
    "msq",
    "open_text",
    "fill_in_blank",
    "ordering",
    "matching",
    "ordered_response",
  ];
  return validTypes.includes(normalized as AssessmentQuestionType)
    ? (normalized as AssessmentQuestionType)
    : undefined;
};

export interface AssessmentInteractiveOption {
  id: string;
  text: string;
}

export interface AssessmentInteractiveBlank {
  id: string;
  label?: string;
}

export interface AssessmentScoreBreakdown {
  mandatory_score: number;
  mandatory_max_score: number;
  bonus_question_score: number;
  bonus_question_max_score: number;
  user_score: number;
  gamification_bonus_score: number;
  contest_points: number;
}

export type AssessmentQuestionCategory = "mandatory" | "bonus";

export interface AssessmentQuestion {
  id?: string;
  question_index?: number;
  question: string;
  answers?: Answer[] | Record<string, string> | string[];
  scoring_rubric?: ScoringRubric;
  grading_guidance?: string;
  competency: string;
  language: string;
  type?: AssessmentQuestionType;
  question_type?: AssessmentQuestionType;
  max_score?: number;
  title?: string;
  instruction?: string;
  prompt?: string;
  template?: string;
  blanks?: AssessmentInteractiveBlank[];
  options?: AssessmentInteractiveOption[];
  slot_count?: number;
  left?: AssessmentInteractiveOption[];
  right?: AssessmentInteractiveOption[];
  is_bonus?: boolean;
  question_category?: AssessmentQuestionCategory;
}

export interface AssessmentLeaderboardItem
  extends Partial<AssessmentScoreBreakdown> {
  user_id: string;
  score: number;
  bonus_score?: number;
  name: string;
}

export interface AssessmentQuestionWinner {
  user_id: string;
  time_spent: number;
  name: string;
}

export type AssessmentStatSummary = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface AssessmentConceptInsight extends AssessmentStatSummary {
  concept?: string;
  correct?: number;
  total?: number;
  correct_answers?: number;
  total_questions?: number;
  score?: number;
  all_score?: number;
}

export interface AssessmentHistoryItem
  extends Partial<AssessmentScoreBreakdown> {
  assessment_id: string;
  title: string;
  focus: string;
  finished_at: string;
  score_earned: number;
  score_max: number;
  bonus_score?: number;
}

export interface AssessmentHistoryResponse {
  items: AssessmentHistoryItem[];
}

export interface AssessmentTeamSummary {
  invited: number;
  completed: number;
  in_progress: number;
  not_started: number;
  questions_count: number;
}

export interface AssessmentTeamItem {
  assessment_id: string;
  title: string;
  focus: string;
  is_scheduled: boolean;
  is_live: boolean;
  is_finished: boolean;
  updated_at: string | null;
  finished_at: string | null;
  summary?: AssessmentTeamSummary;
}

export interface AssessmentTeamResponse {
  items: AssessmentTeamItem[];
}

export interface AssessmentHistoryCompetencySummary {
  competency: string;
  correct: number;
  total: number;
  score_earned?: number;
  score_max?: number;
  bonus_score?: number;
  needs_review: boolean;
}

export interface AssessmentHistoryQuestionReview {
  question_index: number;
  question: string;
  competency: string;
  question_type?: AssessmentQuestionType;
  chosen_answer: string;
  correct_answer?: string | null;
  score: number;
  max_score: number;
  bonus_score?: number;
  is_correct: boolean;
  justification?: string | null;
  answers?: Answer[] | null;
  is_bonus: boolean;
  question_category: AssessmentQuestionCategory;
}

export interface AssessmentHistoryDetail
  extends Partial<AssessmentScoreBreakdown> {
  assessment_id: string;
  title: string;
  focus: string;
  finished_at: string;
  score_earned: number;
  score_max: number;
  bonus_score?: number;
  competency_summary: AssessmentHistoryCompetencySummary[];
  questions: AssessmentHistoryQuestionReview[];
}

export interface AssessmentStatResponse {
  leaderboard?: AssessmentLeaderboardItem[];
  fastest_tap?: AssessmentStatSummary | null;
  most_accurate?: AssessmentStatSummary | null;
  best_comeback?: AssessmentStatSummary | null;
  concepts?: AssessmentStatSummary[];
  concept_summary?: AssessmentStatSummary | null;
  strongest_concept?: AssessmentStatSummary | null;
  needs_review_concept?: AssessmentStatSummary | null;
  competency?: AssessmentStatSummary | null;
  streak?: AssessmentStatSummary | null;
  assessment_finished?: boolean;
  time_left?: number | null;
}

export type AssessmentShowFeedback =
  | "not_show"
  | "after_answer"
  | "after_completion";

export interface AssessmentData extends Partial<AssessmentScoreBreakdown> {
  time_limit: number;
  assessment_id: string;
  title?: string;
  questions: AssessmentQuestion[];
  total_questions_number?: number;
  answered_questions_number?: number;
  scores_earned?: number;
  bonus_score?: number;
  mandatory_questions_number?: number;
  bonus_questions_number?: number;
  time_left?: number | null;
  total_time_spent?: number | null;
  current_user_place?: number | null;
  leaderboard?: AssessmentLeaderboardItem[];
  gamification_enabled?: boolean;
  answer_editing_enabled?: boolean;
  show_feedback?: AssessmentShowFeedback;
  previous_answers?: AssessmentAnswerPayload[];
}

export interface AssessmentListItem {
  assessment_id: string;
  title: string;
}

export interface AssessmentListPayload {
  type: "assessment_list";
  assessments: AssessmentListItem[];
  domain?: string;
  focus?: string;
}

export const normalizeAssessmentListItem = (
  value: unknown,
): AssessmentListItem | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  const raw = value as Record<string, unknown>;
  const assessmentId =
    typeof raw.assessment_id === "string" ? raw.assessment_id.trim() : "";
  if (!assessmentId) {
    return null;
  }
  const title =
    typeof raw.title === "string" && raw.title.trim()
      ? raw.title.trim()
      : "Contest";
  return {
    assessment_id: assessmentId,
    title,
  };
};

export const normalizeAssessmentList = (
  value: unknown,
): AssessmentListItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(normalizeAssessmentListItem)
    .filter((item): item is AssessmentListItem => item !== null);
};

export interface AssessmentAnswerItem {
  question_index: number;
  question_id?: string;
  chosen_answer: string;
  concept: string;
  score: number | null;
  all_score: number;
}

export type AssessmentQuestionAnswerBlock = {
  type: AssessmentQuestionType;
  question_index: number;
  question_id?: string;
  question?: string;
  competency?: string;
  language?: string;
  title?: string;
  instruction?: string;
  prompt?: string;
  template?: string;
  blanks?: AssessmentInteractiveBlank[];
  options?: AssessmentInteractiveOption[];
  slot_count?: number;
  left?: AssessmentInteractiveOption[];
  right?: AssessmentInteractiveOption[];
  answers?: Answer[] | Record<string, string> | string[];
  chosen_answer?: string;
  scoring_rubric?: ScoringRubric;
  grading_guidance?: string;
};

export interface AssessmentAnswerPayload {
  assessment_id: string;
  answers: Array<AssessmentAnswerItem | AssessmentQuestionAnswerBlock>;
  payload?: InteractionSubmitPayload;
  total_questions_number?: number;
  answered_questions_number?: number;
  scores_earned?: number;
  time_left?: number | null;
  time_spent?: number;
  is_submitted?: boolean;
  // Protocol v2: stable IDs for idempotent saves / incremental time.
  save_id?: string;
  visit_id?: string;
  segment_id?: string;
  expected_revision?: number;
  protocol_version?: number;
}

export type AssessmentAttemptState =
  | "not_started"
  | "active"
  | "finished"
  | "expired";

export interface AssessmentAttemptAnswer {
  question_index: number;
  question_id?: string;
  user_answer?: string;
  is_submitted?: boolean;
  time_spent?: number;
  revision?: number;
}

export interface AssessmentAttemptStatus {
  assessment_id: string;
  state: AssessmentAttemptState;
  starts_at: string | null;
  deadline: string | null;
  server_now: string;
  time_left: number;
  protocol_version?: number;
  answers?: AssessmentAttemptAnswer[];
}

export interface AssessmentAnswerResponse
  extends Partial<AssessmentScoreBreakdown> {
  score?: number;
  bonus_score?: number;
  answers?: Answer[] | Record<string, string> | string[];
  justification?: string;
  current_user_place?: number | null;
  leaderboard?: AssessmentLeaderboardItem[];
  question_winner?: AssessmentQuestionWinner | null;
  streak?: number | null;
  time_left?: number | null;
  concepts?: AssessmentStatSummary[];
  strongest_concept?: AssessmentStatSummary | null;
  needs_review_concept?: AssessmentStatSummary | null;
  fastest_tap?: AssessmentStatSummary | null;
  most_accurate?: AssessmentStatSummary | null;
  best_comeback?: AssessmentStatSummary | null;
  // Protocol v2 save acknowledgment.
  accepted?: boolean;
  save_id?: string;
  question_index?: number;
  revision?: number;
  is_submitted?: boolean;
  time_spent?: number;
  replayed?: boolean;
  attempt?: AssessmentAttemptStatus | Record<string, unknown>;
}

export interface AssessmentManagerDashboardSummary {
  invited: number;
  completed: number;
  in_progress: number;
  not_started: number;
  completion_percent: number;
  questions_count: number;
}

export type AssessmentManagerDashboardQuestionRef =
  | string
  | Record<string, unknown>;

export interface AssessmentManagerDashboardQuestionSnapshot {
  question_index?: number;
  question: AssessmentManagerDashboardQuestionRef;
  answered_count: number;
  invited_count: number;
  answered_percent: number;
  average_score: number;
  max_score: number;
  average_bonus_score?: number;
  accuracy: number;
  is_bonus?: boolean;
  question_category?: AssessmentQuestionCategory;
}

export interface AssessmentManagerDashboardParticipant
  extends Partial<AssessmentScoreBreakdown> {
  user_id: string;
  name: string;
  status: string;
  answered_count: number;
  questions_count: number;
  progress_percent: number;
  score: number;
  max_score: number;
  bonus_score?: number;
  current_question?: string | null;
  current_question_index?: number | null;
  last_active: string | null;
}

export interface AssessmentManagerDashboardAttentionItem {
  user_id: string;
  name: string;
  reason: string;
  status: string;
  answered_count: number;
  questions_count: number;
  current_question?: string | null;
  current_question_index?: number | null;
  last_active: string | null;
}

export interface AssessmentManagerDashboardData {
  assessment: {
    id: string;
    title: string;
    focus: string;
    is_scheduled: boolean;
    is_live: boolean;
    is_finished: boolean;
    updated_at: string;
    mandatory_questions_number?: number;
    bonus_questions_number?: number;
    mandatory_max_score?: number;
    bonus_question_max_score?: number;
  };
  summary: AssessmentManagerDashboardSummary;
  team_performance?: {
    average_score: number;
    max_score: number;
    average_bonus_score?: number;
    accuracy: number;
    average_mandatory_score?: number;
    average_bonus_question_score?: number;
    average_user_score?: number;
    average_gamification_bonus_score?: number;
    average_contest_points?: number;
    mandatory_max_score?: number;
    bonus_question_max_score?: number;
  };
  leaderboard?: {
    items: AssessmentLeaderboardItem[];
    top_performers: AssessmentLeaderboardItem[];
  };
  participants: {
    items: AssessmentManagerDashboardParticipant[];
    recent_activity: AssessmentManagerDashboardParticipant[];
  };
  questions: {
    current: AssessmentManagerDashboardQuestionSnapshot;
    progress: AssessmentManagerDashboardQuestionSnapshot[];
    performance: Array<
      AssessmentManagerDashboardQuestionSnapshot & {
        status: "good" | "warning" | "critical" | string;
      }
    >;
    hardest?: Array<
      AssessmentManagerDashboardQuestionSnapshot & {
        status: "good" | "warning" | "critical" | string;
      }
    >;
  };
  attention?: {
    items: AssessmentManagerDashboardAttentionItem[];
  };
}

export const getPreassessment = async (): Promise<PreassessmentData> => {
  try {
    const response = await baseFetch.get("/preassessment");
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching preassessment:", error.message);
    } else {
      console.error("Unknown error fetching preassessment:", error);
    }
    throw error;
  }
};

export const getPreassessmentForce = async (): Promise<PreassessmentData> => {
  try {
    const response = await baseFetch.get("/preassessment_force");
    // console.log("getPreassessmentForce", response.data);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(
        "Axios error fetching preassessment (force):",
        error.message
      );
    } else {
      console.error("Unknown error fetching preassessment (force):", error);
    }
    throw error;
  }
};

export const sendPreassessmentAnswer = async (
  data: PreassessmentAnswerPayload
): Promise<string> => {
  try {
    const response = await baseFetch.post("/preassessment_answer", data);
    void import("../utils/socket").then(({ pingSocketAfterSubmit }) => {
      pingSocketAfterSubmit();
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error sending preassessment answer:", error.message);
    } else {
      console.error("Unknown error sending preassessment answer:", error);
    }
    throw error;
  }
};

export const sendAssessmentAnswer = async (
  data: AssessmentAnswerPayload
): Promise<string | AssessmentAnswerResponse> => {
  try {
    const response = await baseFetch.post("/assessment_answer", data);
    // Dynamic import avoids circular dependency with socket.ts
    void import("../utils/socket").then(({ pingSocketAfterSubmit }) => {
      pingSocketAfterSubmit();
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error sending assessment answer:", error.message);
    } else {
      console.error("Unknown error sending assessment answer:", error);
    }
    throw error;
  }
};

export const getAssessmentHistory =
  async (): Promise<AssessmentHistoryResponse> => {
    try {
      const response = await baseFetch.get("/assessments/history");
      const data = response.data;
      const items = Array.isArray(data?.items)
        ? data.items
            .map(normalizeHistoryItem)
            .filter(
              (item: AssessmentHistoryItem | null): item is AssessmentHistoryItem =>
                item !== null,
            )
        : [];
      return { items };
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(
          "Axios error fetching assessment history:",
          error.message,
        );
      } else {
        console.error("Unknown error fetching assessment history:", error);
      }
      throw error;
    }
  };

const normalizeTeamSummary = (
  value: unknown,
): AssessmentTeamSummary | undefined => {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  return {
    invited: numberOrZero(raw.invited),
    completed: numberOrZero(raw.completed),
    in_progress: numberOrZero(raw.in_progress),
    not_started: numberOrZero(raw.not_started),
    questions_count: numberOrZero(raw.questions_count),
  };
};

const normalizeTeamItem = (value: unknown): AssessmentTeamItem | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  const raw = value as Record<string, unknown>;
  const assessmentId =
    typeof raw.assessment_id === "string" ? raw.assessment_id.trim() : "";
  if (!assessmentId) {
    return null;
  }

  const isLive = raw.is_live === true;
  const isScheduled = raw.is_scheduled === true;
  const isFinished =
    typeof raw.is_finished === "boolean"
      ? raw.is_finished
      : !isLive && !isScheduled;

  return {
    assessment_id: assessmentId,
    title: typeof raw.title === "string" && raw.title.trim() ? raw.title : "Contest",
    focus: typeof raw.focus === "string" ? raw.focus : "",
    is_scheduled: isScheduled || (!isLive && !isFinished),
    is_live: isLive,
    is_finished: isFinished,
    updated_at: typeof raw.updated_at === "string" ? raw.updated_at : null,
    finished_at: typeof raw.finished_at === "string" ? raw.finished_at : null,
    summary: normalizeTeamSummary(raw.summary),
  };
};

const teamItemStatusRank = (item: AssessmentTeamItem): number => {
  if (item.is_live && !item.is_finished) return 0;
  if (item.is_scheduled && !item.is_finished && !item.is_live) return 1;
  return 2;
};

const sortTeamItems = (items: AssessmentTeamItem[]): AssessmentTeamItem[] =>
  [...items].sort((a, b) => {
    const rankDiff = teamItemStatusRank(a) - teamItemStatusRank(b);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    const aTime = Date.parse(a.updated_at ?? a.finished_at ?? "") || 0;
    const bTime = Date.parse(b.updated_at ?? b.finished_at ?? "") || 0;
    return bTime - aTime;
  });

export const getAssessmentsTeam = async (): Promise<AssessmentTeamResponse> => {
  try {
    const response = await baseFetch.get("/assessments/team");
    const data = response.data;
    const items = Array.isArray(data?.items)
      ? sortTeamItems(
          data.items
            .map(normalizeTeamItem)
            .filter(
              (item: AssessmentTeamItem | null): item is AssessmentTeamItem =>
                item !== null,
            ),
        )
      : [];
    return { items };
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching team assessments:", error.message);
    } else {
      console.error("Unknown error fetching team assessments:", error);
    }
    throw error;
  }
};

const numberOrZero = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const SCORE_BREAKDOWN_KEYS = [
  "mandatory_score",
  "mandatory_max_score",
  "bonus_question_score",
  "bonus_question_max_score",
  "user_score",
  "gamification_bonus_score",
  "contest_points",
] as const;

const hasScoreBreakdownFields = (raw: Record<string, unknown>): boolean =>
  SCORE_BREAKDOWN_KEYS.some(
    (key) => typeof raw[key] === "number" && Number.isFinite(raw[key]),
  );

const normalizeScoreBreakdown = (
  raw: Record<string, unknown>,
): AssessmentScoreBreakdown => ({
  mandatory_score: numberOrZero(raw.mandatory_score),
  mandatory_max_score: numberOrZero(raw.mandatory_max_score),
  bonus_question_score: numberOrZero(raw.bonus_question_score),
  bonus_question_max_score: numberOrZero(raw.bonus_question_max_score),
  user_score: numberOrZero(raw.user_score),
  gamification_bonus_score: numberOrZero(raw.gamification_bonus_score),
  contest_points: numberOrZero(raw.contest_points),
});

const maybeNormalizeScoreBreakdown = (
  raw: Record<string, unknown>,
): Partial<AssessmentScoreBreakdown> =>
  hasScoreBreakdownFields(raw) ? normalizeScoreBreakdown(raw) : {};

const normalizeHistoryAnswer = (value: unknown): Answer | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (typeof raw.text !== "string") {
    return null;
  }
  const points = typeof raw.points === "number" ? raw.points : 0;
  return { text: raw.text, points };
};

const normalizeHistoryItem = (value: unknown): AssessmentHistoryItem | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  const raw = value as Record<string, unknown>;
  const assessmentId =
    typeof raw.assessment_id === "string"
      ? raw.assessment_id
      : typeof raw.id === "string"
        ? raw.id
        : "";
  if (!assessmentId) {
    return null;
  }

  return {
    assessment_id: assessmentId,
    title: typeof raw.title === "string" ? raw.title : "",
    focus: typeof raw.focus === "string" ? raw.focus : "",
    finished_at: typeof raw.finished_at === "string" ? raw.finished_at : "",
    score_earned: typeof raw.score_earned === "number" ? raw.score_earned : 0,
    score_max: typeof raw.score_max === "number" ? raw.score_max : 0,
    bonus_score: typeof raw.bonus_score === "number" ? raw.bonus_score : 0,
    ...maybeNormalizeScoreBreakdown(raw),
  };
};

const resolveHistoryChosenAnswer = (raw: Record<string, unknown>): string => {
  for (const key of [
    "chosen_answer",
    "user_answer",
    "answer",
    "selected_answer",
    "text_answer",
  ]) {
    const value = raw[key];
    if (typeof value === "string") {
      return value;
    }
  }
  return "";
};

const resolveHistoryQuestionText = (raw: Record<string, unknown>): string =>
  extractAssessmentQuestion(raw);

const normalizeHistoryQuestion = (
  value: unknown,
): AssessmentHistoryQuestionReview | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  const raw = value as Record<string, unknown>;
  const question = resolveHistoryQuestionText(raw);
  if (!question) {
    return null;
  }

  const questionType = normalizeAssessmentQuestionType(
    raw.type ?? raw.question_type,
  );
  const answersRaw = raw.answers;
  const answers = Array.isArray(answersRaw)
    ? answersRaw
        .map(normalizeHistoryAnswer)
        .filter((item): item is Answer => item !== null)
    : null;
  const isBonus = raw.is_bonus === true;

  return {
    question_index:
      typeof raw.question_index === "number" ? raw.question_index : 0,
    question,
    competency: typeof raw.competency === "string" ? raw.competency : "",
    question_type: questionType,
    chosen_answer: resolveHistoryChosenAnswer(raw),
    correct_answer:
      typeof raw.correct_answer === "string" ? raw.correct_answer : null,
    score: typeof raw.score === "number" ? raw.score : 0,
    max_score: typeof raw.max_score === "number" ? raw.max_score : 0,
    bonus_score: typeof raw.bonus_score === "number" ? raw.bonus_score : 0,
    is_correct: Boolean(raw.is_correct),
    justification:
      typeof raw.justification === "string" ? raw.justification : null,
    answers: answers && answers.length > 0 ? answers : null,
    is_bonus: isBonus,
    question_category:
      raw.question_category === "bonus" || isBonus ? "bonus" : "mandatory",
  };
};

const normalizeCompetencySummary = (
  value: unknown,
): AssessmentHistoryCompetencySummary | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (typeof raw.competency !== "string") {
    return null;
  }

  return {
    competency: raw.competency,
    correct: typeof raw.correct === "number" ? raw.correct : 0,
    total: typeof raw.total === "number" ? raw.total : 0,
    score_earned:
      typeof raw.score_earned === "number" ? raw.score_earned : undefined,
    score_max: typeof raw.score_max === "number" ? raw.score_max : undefined,
    bonus_score:
      typeof raw.bonus_score === "number" ? raw.bonus_score : undefined,
    needs_review: Boolean(raw.needs_review),
  };
};

const normalizeAssessmentHistoryDetail = (
  data: unknown,
): AssessmentHistoryDetail => {
  const raw =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const competencySummary = Array.isArray(raw.competency_summary)
    ? raw.competency_summary
        .map(normalizeCompetencySummary)
        .filter(
          (item): item is AssessmentHistoryCompetencySummary => item !== null,
        )
    : [];
  const questions = Array.isArray(raw.questions)
    ? raw.questions
        .map(normalizeHistoryQuestion)
        .filter(
          (item): item is AssessmentHistoryQuestionReview => item !== null,
        )
    : [];

  return {
    assessment_id:
      typeof raw.assessment_id === "string" ? raw.assessment_id : "",
    title: typeof raw.title === "string" ? raw.title : "",
    focus: typeof raw.focus === "string" ? raw.focus : "",
    finished_at:
      typeof raw.finished_at === "string" ? raw.finished_at : "",
    score_earned:
      typeof raw.score_earned === "number" ? raw.score_earned : 0,
    score_max: typeof raw.score_max === "number" ? raw.score_max : 0,
    bonus_score: typeof raw.bonus_score === "number" ? raw.bonus_score : 0,
    ...maybeNormalizeScoreBreakdown(raw),
    competency_summary: competencySummary,
    questions,
  };
};

export const getAssessmentHistoryDetail = async (
  assessmentId: string,
): Promise<AssessmentHistoryDetail> => {
  try {
    const response = await baseFetch.get(
      `/assessments/history/${encodeURIComponent(assessmentId)}`,
    );
    return normalizeAssessmentHistoryDetail(response.data);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(
        "Axios error fetching assessment history detail:",
        error.message,
      );
    } else {
      console.error(
        "Unknown error fetching assessment history detail:",
        error,
      );
    }
    throw error;
  }
};

export const getAssessmentStat = async (
  assessmentId: string
): Promise<AssessmentStatResponse> => {
  try {
    const response = await baseFetch.get("/assessment_stat", {
      params: {
        assessment_id: assessmentId,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching assessment stats:", error.message);
    } else {
      console.error("Unknown error fetching assessment stats:", error);
    }
    throw error;
  }
};

export const getAssessmentManagerDashboard = async (
  assessmentId: string,
  questionIndex: number,
): Promise<AssessmentManagerDashboardData> => {
  try {
    const response = await baseFetch.get("/assessment_manager_dashboard", {
      params: {
        assessment_id: assessmentId,
        question_index: questionIndex,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(
        "Axios error fetching assessment manager dashboard:",
        error.message
      );
    } else {
      console.error(
        "Unknown error fetching assessment manager dashboard:",
        error
      );
    }
    throw error;
  }
};

export interface AssessmentReportResponse {
  xlsx: string;
  filename: string;
}

export const getAssessmentReport = async (
  assessmentId: string,
): Promise<AssessmentReportResponse> => {
  try {
    const response = await baseFetch.get("/assessment_report", {
      params: {
        assessment_id: assessmentId,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(
        "Axios error fetching assessment report:",
        error.message,
      );
    } else {
      console.error("Unknown error fetching assessment report:", error);
    }
    throw error;
  }
};

export const sendAssessmentById = async (
  assessmentId: string,
): Promise<void> => {
  try {
    await baseFetch.get("/assessments/send", {
      params: {
        assessment_id: assessmentId,
      },
    });
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error sending assessment by id:", error.message);
    } else {
      console.error("Unknown error sending assessment by id:", error);
    }
    throw error;
  }
};

export type FinishAssessmentResponse = AssessmentAnswerResponse & {
  assessment_finished?: boolean;
  detail?: string;
};

export const finishAssessment = async (
  assessmentId: string,
): Promise<FinishAssessmentResponse | void> => {
  try {
    const response = await baseFetch.post(
      `/assessments/${encodeURIComponent(assessmentId)}/finish`,
    );
    void import("../utils/socket").then(({ pingSocketAfterSubmit }) => {
      pingSocketAfterSubmit();
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error finishing assessment:", error.message);
    } else {
      console.error("Unknown error finishing assessment:", error);
    }
    throw error;
  }
};

const normalizeAttemptStatus = (
  value: unknown,
  fallbackAssessmentId: string,
): AssessmentAttemptStatus => {
  const raw =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};
  const stateRaw = raw.state;
  const state: AssessmentAttemptState =
    stateRaw === "not_started" ||
    stateRaw === "active" ||
    stateRaw === "finished" ||
    stateRaw === "expired"
      ? stateRaw
      : "active";

  const answers = Array.isArray(raw.answers)
    ? raw.answers
        .filter((item) => item && typeof item === "object")
        .map((item) => {
          const row = item as Record<string, unknown>;
          return {
            question_index:
              typeof row.question_index === "number" ? row.question_index : -1,
            question_id:
              typeof row.question_id === "string" ? row.question_id : undefined,
            user_answer:
              typeof row.user_answer === "string" ? row.user_answer : undefined,
            is_submitted:
              typeof row.is_submitted === "boolean"
                ? row.is_submitted
                : undefined,
            time_spent:
              typeof row.time_spent === "number" ? row.time_spent : undefined,
            revision:
              typeof row.revision === "number" ? row.revision : undefined,
          } satisfies AssessmentAttemptAnswer;
        })
        .filter((item) => item.question_index >= 0)
    : undefined;

  return {
    assessment_id:
      typeof raw.assessment_id === "string" && raw.assessment_id.trim()
        ? raw.assessment_id.trim()
        : fallbackAssessmentId,
    state,
    starts_at: typeof raw.starts_at === "string" ? raw.starts_at : null,
    deadline: typeof raw.deadline === "string" ? raw.deadline : null,
    server_now:
      typeof raw.server_now === "string"
        ? raw.server_now
        : new Date().toISOString(),
    time_left:
      typeof raw.time_left === "number" ? Math.max(0, raw.time_left) : 0,
    protocol_version:
      typeof raw.protocol_version === "number"
        ? raw.protocol_version
        : undefined,
    answers,
  };
};

// Starts the personal attempt clock (idempotent). Call on Start Challenge.
export const startAssessmentAttempt = async (
  assessmentId: string,
): Promise<AssessmentAttemptStatus> => {
  try {
    const response = await baseFetch.post(
      `/assessments/${encodeURIComponent(assessmentId)}/start`,
    );
    return normalizeAttemptStatus(response.data, assessmentId);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error starting assessment attempt:", error.message);
    } else {
      console.error("Unknown error starting assessment attempt:", error);
    }
    throw error;
  }
};

// Canonical attempt status — use on reload, device handoff, and timeout.
export const getAssessmentAttempt = async (
  assessmentId: string,
): Promise<AssessmentAttemptStatus> => {
  try {
    const response = await baseFetch.get(
      `/assessments/${encodeURIComponent(assessmentId)}/attempt`,
    );
    return normalizeAttemptStatus(response.data, assessmentId);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching assessment attempt:", error.message);
    } else {
      console.error("Unknown error fetching assessment attempt:", error);
    }
    throw error;
  }
};
